import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

type Variant = 'gold' | 'ghost' | 'outline' | 'danger' | 'secondary';
type Size    = 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?:  Variant;
  size?:     Size;
  loading?:  boolean;
  icon?:     React.ReactNode;
  iconEnd?:  React.ReactNode;
  children?: React.ReactNode;
  fullWidth?: boolean;
}

const variantClasses: Record<Variant, string> = {
  gold:      'gold-button text-navy-900 font-bold',
  ghost:     'bg-transparent text-champagne-200 hover:bg-white/5 hover:text-gold-200',
  outline:   'bg-transparent border border-gold-500/40 text-gold-200 hover:border-gold-300 hover:bg-gold-500/10',
  danger:    'bg-red-900/60 border border-red-500/40 text-red-200 hover:bg-red-800/70',
  secondary: 'bg-navy-700/80 border border-white/10 text-champagne-200 hover:bg-navy-600/80',
};

const sizeClasses: Record<Size, string> = {
  sm:  'px-3 py-1.5 text-xs rounded-lg gap-1.5',
  md:  'px-4 py-2.5 text-sm rounded-xl gap-2',
  lg:  'px-6 py-3 text-base rounded-xl gap-2',
  xl:  'px-8 py-4 text-lg rounded-2xl gap-3',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'gold',
  size = 'md',
  loading = false,
  icon,
  iconEnd,
  children,
  fullWidth,
  disabled,
  className = '',
  ...props
}) => (
  <motion.button
    whileTap={{ scale: 0.97 }}
    whileHover={{ scale: 1.02 }}
    transition={{ duration: 0.15 }}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    className={`
      inline-flex min-h-11 items-center justify-center font-medium
      transition-all duration-200 select-none
      disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none
      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400
      ${variantClasses[variant]}
      ${sizeClasses[size]}
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `}
    {...props}
  >
    {loading ? (
      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    ) : icon}
    {children}
    {!loading && iconEnd}
  </motion.button>
);
